import { useCallback, useState } from "react";
import useDeleteEmailTemplate from "@babackapp/private-api-client/client/use-delete-email-template";
import useCurrentShopSuspense from "./use-current-shop-suspense";
import useTemplateWorkflowsDependencies from "./use-template-workflows-dependencies";

export function useDeleteTemplate(templateId: string, isProtected = false) {
  const { data: shop } = useCurrentShopSuspense();
  const deleteEmailTemplate = useDeleteEmailTemplate(shop.id);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const dependantWorkflows = useTemplateWorkflowsDependencies(templateId);

  // A return template can also be linked through the shop's return email
  // configs (the dropdowns on the customer-portal settings page), not only via
  // workflows. Deleting it while it's still selected there would orphan that
  // `templateId` reference, so treat it as a linked notification too.
  const isLinkedToReturnConfig = Object.values(
    shop.preferences?.notifications?.returnEmailConfigs ?? {},
  ).some((config) => config?.templateId === templateId);

  const hasLinkedNotifications =
    dependantWorkflows.length > 0 || isLinkedToReturnConfig;
  const canDelete = !hasLinkedNotifications && !isProtected;

  const handleDeleteClick = useCallback(() => {
    // Self-enforce the full guard (not only `isProtected`) so a linked
    // template can never be deleted even if a caller opens the dialog directly.
    if (!canDelete) {
      return;
    }
    deleteEmailTemplate.mutate(templateId);
    setIsDeleteDialogOpen(false);
  }, [templateId, deleteEmailTemplate, canDelete]);

  const openDeleteDialog = useCallback(() => {
    if (canDelete) {
      setIsDeleteDialogOpen(true);
    }
  }, [canDelete]);

  return {
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    handleDeleteClick,
    openDeleteDialog,
    hasLinkedNotifications,
    isLinkedToReturnConfig,
    canDelete,
    isProtected,
    dependantWorkflows,
  };
}
